/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import * as React from 'react';
import axios from 'axios';
import { useDispatch } from 'react-redux';
import type { ThunkDispatch, UnknownAction } from '@reduxjs/toolkit';

import {
    SecurityHelper,
    Button,
    Checkbox,
    Heading,
    FlexRow,
    FlexCol,
    Spinner,
    StyledModal,
    TextField,
    Spacer,
    RadioPillGroup,
    TextBlock,
    CartActions,
    ModalActions,
    ButtonGroup,
} from 'sarsaparilla';

import { useRecaptchaWithFallback, RecaptchaCheckbox } from 'shared-ui/recaptcha';
import { useAuth } from 'shared-ui/auth';

interface donationModalProps {
    /** Donation Location Information */
    facilityID: string;
    agencyID: string;
    assetID: string;
    assetName: string;
    agencyName: string;

    /** Modal source **/
    modalSource?: string;

    /**  Used to override **/
    overrideData?: any;

    /** Used if the container only wants the content of the modal (and not a modal itself) */
    contentOnly?: boolean;
    overrideWidth?: 'md' | 'sm' | 'lg' | 'xl' | 'none' | undefined;

    /** IsOpen determines if the modal is open **/
    isOpen: boolean;
    /** IsLoading determines if the modal content is a spinner **/
    isLoading?: boolean;
    /** OnClose is a function that should be called when the modal has been requested to close */
    onRequestClose?: any;
}

export interface DonationSettings {
    is_donatable: boolean;
    asset_id: string;
    asset_name: string;
    name: string;
    facility_id: string;
}

function generateDonationRadioPillOptions(
    donation_options: any,
    allowOtherDonation: boolean
) {
    const elements: any[] = [];

    if (!donation_options) {
        return elements;
    }

    donation_options.forEach((opt: any) => {
        if (SecurityHelper.isGuest()) {
            if (opt > 100) {
                return;
            }
        }

        const labelValue = `$${opt}`;

        elements.push({
            label: labelValue,
            value: `${opt}`,
        });
    });

    if (allowOtherDonation) {
        elements.push({
            label: 'Other',
            value: `${'other'}`,
        });
    }

    return elements;
}

function makeValidator(donation_limit: number | boolean) {
    return (input: any) => {
        const parsed = {
            isValid: true,
            formattedValue: String(input),
            errorText: '',
        };

        const fl = Number(input);
        if (typeof fl !== 'number' || Number.isNaN(fl)) {
            parsed.isValid = false;
            parsed.errorText = 'Value must be a number';
        } else if (fl < 0) {
            parsed.isValid = false;
            parsed.errorText = 'Value must be greater than 0';
        } else if (donation_limit && fl > Number(donation_limit)) {
            parsed.isValid = false;
            parsed.errorText = `Value must not exceed $${donation_limit}`;
        } else if (fl > Number(10000)) {
            parsed.isValid = false;
            parsed.errorText = 'Value must be less than $10,000';
        } else if (SecurityHelper.isGuest()) {
            if (fl > Number(100)) {
                parsed.isValid = false;
                parsed.errorText = 'Value must be less than $100 for guests';
            }
        }

        parsed.formattedValue = String(fl.toFixed(2));

        return parsed;
    };
}

export default function DonationModal({
    agencyID = '',
    assetName = '',
    assetID = '',
    facilityID = '',
    agencyName = '',
    modalSource = 'unknown-web',
    isOpen = false,
    isLoading = false,
    contentOnly = false,
    overrideData = null,
    overrideWidth = 'md',
    onRequestClose = () => {},
}: donationModalProps) {
    const { openLoginWithGuestModal } = useAuth();

    const [donationRadioValue, setDonationRadioValue] = React.useState<string>('1');
    const [otherValue, setOtherValue] = React.useState();
    const [otherValid, setOtherValid] = React.useState<boolean>();
    const [acceptTAC, setAcceptTAC] = React.useState<boolean>();
    const [addingToCart, setAddingToCart] = React.useState<boolean>(false);
    const [fetchError, setFetchError] = React.useState<boolean>(false);
    const [isFetching, setIsFetching] = React.useState<boolean>(false);
    const [agencyData, setAgencyData] = React.useState<any>();

    const [isChallengeModalOpen, setIsChallengeModalOpen] = React.useState(false);
    const {
        getToken,
        handleRecaptchaError,
        isChallengeRequired,
        isV2Invalid,
        recaptchaCheckboxProps,
    } = useRecaptchaWithFallback();

    const dispatch = useDispatch<ThunkDispatch<unknown, unknown, UnknownAction>>();

    const fetchData = React.useCallback(() => {
        if (!isOpen) return;

        setIsFetching(true);

        const url = `${process.env.API}/donation/modal/${agencyID}`;

        axios
            .get(url, { headers: { Authorization: SecurityHelper.getAuthHeader() } })
            .then((e) => {
                setAgencyData(e.data);
            })
            .catch(() => {
                setFetchError(true);
            })
            .finally(() => {
                setIsFetching(false);
            });
    }, [agencyID, isOpen]);

    const makeDonation = React.useCallback(async () => {
        if (!SecurityHelper.isLoggedIn() && !SecurityHelper.isGuest()) {
            openLoginWithGuestModal();
            return;
        }

        let accountID = SecurityHelper.getAccount().account_id;
        if (SecurityHelper.isGuest()) {
            accountID = SecurityHelper.getGuestAccount().account.account_id;
        }

        // If a v2 challenge is required, pop the challenge modal and defer the
        // reservation until the user solves the checkbox. Mirrors BookingWrapper.
        if (isChallengeRequired && !isChallengeModalOpen) {
            setIsChallengeModalOpen(true);
            return;
        }

        // getToken throws if the reCAPTCHA script failed to load. Don't block the
        // reservation on it - submit without a token and let the backend decide.
        let recaptchaToken = null;
        try {
            recaptchaToken = await getToken('create_reservation');
        } catch {
            recaptchaToken = null;
        }

        const payload = {
            asset_id: assetID,
            facility_id: facilityID,
            asset_name: assetName,
            agency_name: agencyName,
            account_id: accountID,
            amount: Number(0).toFixed(2),
            order_details: {
                source: modalSource,
            },

            recaptcha_token: recaptchaToken,
            recaptcha_version: isChallengeRequired ? 'V2' : 'V3',
        };

        if (donationRadioValue !== 'other') {
            const donationAmount = Number(donationRadioValue).toFixed(2);
            payload.amount = donationAmount;
        } else {
            const donationAmount = Number(otherValue).toFixed(2);
            payload.amount = donationAmount;
        }

        const url = `${process.env.API}/donation/reservation`;

        setAddingToCart(true);
        setIsChallengeModalOpen(false);
        setFetchError(false);
        axios
            .post(url, payload, {
                headers: { Authorization: SecurityHelper.getAuthHeader() },
            })
            .then(() => {
                setAddingToCart(false);
                dispatch(CartActions.fetchCart()).then(onRequestClose);
            })
            .catch((e) => {
                setAddingToCart(false);
                const { shouldRetry } = handleRecaptchaError(e);
                if (shouldRetry) {
                    // Score was too low - pop the v2 challenge modal for the retry
                    setIsChallengeModalOpen(true);
                    return;
                }
                setFetchError(true);
            });
    }, [
        assetID,
        facilityID,
        assetName,
        agencyName,
        modalSource,
        donationRadioValue,
        otherValue,
        onRequestClose,
        dispatch,
        isChallengeRequired,
        isChallengeModalOpen,
        getToken,
        handleRecaptchaError,
        openLoginWithGuestModal,
    ]);

    React.useEffect(() => {
        if (overrideData) {
            setAgencyData(overrideData);
        } else {
            fetchData();
        }
    }, [fetchData, overrideData]);

    function handleOtherValueChange(e: any) {
        const newOtherValid = makeValidator(agencyData?.agency_maximum)(
            e.target.value
        ).isValid;

        setOtherValid(newOtherValid);
        setOtherValue(e.target.value);
    }

    const closeChallengeModal = () => {
        setIsChallengeModalOpen(false);
    };

    const onSolveChallenge = () => {
        makeDonation();
    };

    let donationAmountBox = null;
    if (donationRadioValue === 'other') {
        donationAmountBox = (
            <div>
                <FlexRow justifyContent="center">
                    <TextField
                        id="donate-other-amount"
                        unitBefore="$"
                        srUnitDescription="dollars"
                        label="Other donation value"
                        isLabelVisible={false}
                        isDisabled={donationRadioValue !== 'other'}
                        validator={makeValidator(agencyData?.agency_maximum)}
                        value={otherValue}
                        onChange={handleOtherValueChange}
                        hintText={
                            agencyData?.has_maximum
                                ? `Not to exceed $${agencyData?.agency_maximum}`
                                : 'Enter a donation amount'
                        }
                    />
                </FlexRow>
                <Spacer size="md" />
            </div>
        );
    }

    const MODAL = agencyData?.modal_settings;

    let contentBody =
        fetchError || !MODAL ? (
            <FlexRow>
                <FlexCol>
                    <p>
                        <TextBlock width="md" alignX="center">
                            <p>
                                We are currently unable to process donations at this
                                location, please try again later.
                            </p>
                        </TextBlock>
                    </p>
                </FlexCol>
            </FlexRow>
        ) : (
            <div>
                <Heading appearance="h5" headingLevel={2} className="mb-2">
                    {MODAL?.header}
                </Heading>

                <div>
                    <TextBlock width={overrideWidth} alignX="left">
                        {MODAL?.body_text?.map((d: string, i: number) => (
                            <p key={`info-${i}`}>{d}</p>
                        ))}
                    </TextBlock>
                </div>

                {contentOnly && <Spacer size="lg" />}

                <FlexRow justifyContent="center" className="my-2">
                    <RadioPillGroup
                        description="Donation Amount"
                        value={donationRadioValue}
                        onChange={(e) => {
                            setDonationRadioValue(e);
                        }}
                        options={generateDonationRadioPillOptions(
                            MODAL?.donation_options,
                            MODAL?.allow_other_donation
                        )}
                    />
                </FlexRow>

                {donationAmountBox}

                {!contentOnly && <Spacer size="lg" />}

                <FlexRow justifyContent="center">
                    <div>
                        <Checkbox
                            id="tac-checkbox"
                            data-testid="tac-checkbox"
                            label="I accept the terms and conditions"
                            value="controlled"
                            isChecked={acceptTAC}
                            onChange={(e) => {
                                setAcceptTAC(e.target.checked);
                            }}
                        />
                    </div>
                </FlexRow>

                <Spacer size="md" />

                <FlexRow justifyContent="center">
                    <Button
                        isDisabled={
                            !acceptTAC ||
                            (donationRadioValue === 'other' && !otherValid) ||
                            addingToCart ||
                            isLoading ||
                            isFetching
                        }
                        onClick={makeDonation}
                    >
                        Make Donation
                    </Button>
                </FlexRow>

                <Spacer size="md" />

                <FlexRow justifyContent="center">
                    <p>
                        <a
                            href={MODAL?.tac_link}
                            target="_blank"
                            rel="noreferrer"
                            aria-label="Terms & Conditions (opens in a new tab)"
                        >
                            Terms & Conditions
                        </a>
                    </p>
                </FlexRow>
            </div>
        );

    if (isLoading || isFetching) {
        contentBody = <Spinner />;
    }

    if (contentOnly) {
        return contentBody;
    }

    return (
        <>
            <StyledModal
                size="md"
                isOpen={isOpen}
                heading={MODAL?.title}
                onRequestClose={onRequestClose}
            >
                {contentBody}
            </StyledModal>

            {isChallengeModalOpen && (
                <StyledModal
                    isOpen={isChallengeModalOpen}
                    onRequestClose={closeChallengeModal}
                    heading="Confirm You're Not a Robot"
                >
                    Please complete the challenge below to continue with your reservation.
                    <RecaptchaCheckbox
                        {...recaptchaCheckboxProps}
                        isFallbackOnly
                        action="create_reservation"
                    />
                    <ModalActions>
                        <ButtonGroup isFullWidthOnMobile={false} isStretchedToFit>
                            <Button
                                appearance="tertiary"
                                onClick={closeChallengeModal}
                                isDisabled={isLoading}
                                gaTrackingId="venue-recaptcha-cancel"
                            >
                                Cancel
                            </Button>
                            <Button
                                onClick={onSolveChallenge}
                                isDisabled={isV2Invalid}
                                isLoading={isLoading}
                                gaTrackingId="venue-recaptcha-submit"
                            >
                                Continue
                            </Button>
                        </ButtonGroup>
                    </ModalActions>
                </StyledModal>
            )}
        </>
    );
}

// cSpell:ignore donatable
